import { format } from 'date-fns'
import type { DateRange, CalendarPeriod } from '../types'

/**
 * Calculate date range for a given period offset
 * @param offset - 0 = current period, -1 = previous 28 days, +1 = future period
 * @param periodLength - Number of days in period (default: 28 for exactly 4 weeks)
 * @returns DateRange object with start and end dates
 */
export function calculateDateRange(offset: number, periodLength = 28): DateRange {
  const today = new Date()
  
  // For offset = 0: end = today, start = 27 days ago (28 days total including today)
  // For offset = -1: end = 28 days ago, start = 55 days ago (28 days total)
  // For offset = +1: end = 28 days from now, start = 1 day from now (28 days total)
  
  const endDaysFromToday = -offset * periodLength
  const startDaysFromToday = endDaysFromToday + periodLength - 1 // -1 to make it exactly 28 days
  
  const end = new Date(today.getTime() - endDaysFromToday * 24 * 60 * 60 * 1000)
  const start = new Date(today.getTime() - startDaysFromToday * 24 * 60 * 60 * 1000)
  
  return { start, end }
}

/**
 * Create a CalendarPeriod object for the given offset
 * @param offset - Period offset (0 = current, negative = past, positive = future)
 * @returns CalendarPeriod with offset and calculated range
 */
export function createCalendarPeriod(offset: number): CalendarPeriod {
  return {
    offset,
    range: calculateDateRange(offset)
  }
}

/**
 * Check if a date range represents the current period (last 30 days)
 * @param range - DateRange to check
 * @returns true if this is the current period
 */
export function isCurrentPeriod(range: DateRange): boolean {
  const currentRange = calculateDateRange(0)
  return (
    Math.abs(range.start.getTime() - currentRange.start.getTime()) < 24 * 60 * 60 * 1000 &&
    Math.abs(range.end.getTime() - currentRange.end.getTime()) < 24 * 60 * 60 * 1000
  )
}

/**
 * Format a date range for display in the calendar header
 * @param range - DateRange to format
 * @returns Formatted string for display
 */
export function formatPeriodRange(range: DateRange): string {
  if (isCurrentPeriod(range)) {
    return 'Last 30 Days'
  }
  
  const startStr = format(range.start, 'MMM d')
  const endStr = format(range.end, 'MMM d')
  
  // If same month, show "Jan 1 - 31", otherwise show "Dec 31 - Jan 30"
  if (range.start.getMonth() === range.end.getMonth()) {
    return `${startStr} - ${format(range.end, 'd')}`
  }
  
  return `${startStr} - ${endStr}`
}

/**
 * Check if a workout date falls within a given date range
 * @param workoutDate - Date string or Date object of the workout
 * @param range - DateRange to check against
 * @returns true if workout is within the range
 */
export function isWorkoutInRange(workoutDate: string | Date, range: DateRange): boolean {
  const date = typeof workoutDate === 'string' ? new Date(workoutDate) : workoutDate
  
  // Set all times to start of day for accurate comparison
  const workoutDay = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const rangeStart = new Date(range.start.getFullYear(), range.start.getMonth(), range.start.getDate())
  const rangeEnd = new Date(range.end.getFullYear(), range.end.getMonth(), range.end.getDate())
  
  return workoutDay >= rangeStart && workoutDay <= rangeEnd
}

/**
 * Calculate the maximum allowed period offset (how far back user can go)
 * Currently limits to ~9 months since WorkoutService fetches 9 pages of data
 * This could be enhanced to dynamically fetch more data when needed
 * @returns Maximum negative offset allowed
 */
export function getMaxPeriodOffset(): number {
  return -9 // 9 periods of 28 days = ~8.4 months (matches WorkoutService fetch depth)
}

/**
 * Validate a period offset to ensure it's within allowed bounds
 * @param offset - Offset to validate
 * @returns Clamped offset within valid range
 */
export function validatePeriodOffset(offset: number): number {
  const maxOffset = getMaxPeriodOffset()
  return Math.max(maxOffset, Math.min(0, offset))
}